import { createContext, useContext } from "react";
import type { Company, Me } from "../api";
import type { AuthenticatedSession, SessionState } from "./types";

/** Ce que le fournisseur de session expose aux écrans. */
export type SessionApi = {
  state: SessionState;
  /** Ouvre une session. Lève un `ProblemError` en cas de refus. */
  login: (email: string, password: string) => Promise<Me>;
  /** Prend acte d'une session ouverte par un autre chemin (invitation). */
  adopt: (me: Me) => void;
  /** Ferme la session et repasse en anonyme, quoi qu'il arrive au réseau. */
  logout: () => Promise<void>;
  /** Relit `/me` : après une acceptation d'invitation, ou pour réessayer. */
  refresh: () => Promise<void>;
  /** Met à jour l'entreprise de la session, après un changement de marque. */
  setCompany: (company: Company) => void;
};

// Le contexte et ses hooks vivent ici, séparés du fournisseur : la règle
// `react-refresh/only-export-components` veut qu'un fichier de composant
// n'exporte que des composants.
export const SessionContext = createContext<SessionApi | null>(null);

/** La session, où qu'on soit sous `SessionProvider`. */
export function useSession(): SessionApi {
  const ctx = useContext(SessionContext);
  if (ctx === null) {
    throw new Error("useSession doit être appelé sous un SessionProvider.");
  }
  return ctx;
}

/**
 * La session, en la supposant ouverte. Réservé aux composants rendus sous
 * `RequireAuth`, qui garantit l'état : ailleurs, c'est une erreur de
 * programmation, et elle se voit tout de suite.
 */
export function useAuthenticatedSession(): AuthenticatedSession {
  const { state } = useSession();
  if (state.status !== "authenticated") {
    throw new Error("useAuthenticatedSession hors d'une route protégée par RequireAuth.");
  }
  return state;
}
